import { supabase } from '@/integrations/supabase/client';
import { isMainCategory, getSubcategoriesForMainCategory } from '@/utils/categoryUtils';
import { logger } from '@/utils/logger';
import { validateUUID } from '@/utils/sanitization';
import { DataIntegrityError, DatabaseError } from '@/utils/errors';
import { BOOK_STATUS, BookStatus } from '@/types/db.types';
import type { Database } from '@/integrations/supabase/types';

// Use Supabase generated type for the view
type BooksWithSellerRow = Database['public']['Views']['books_with_seller']['Row'];

// FTS RPC returns a slightly different shape (includes rank, excludes some view fields)
type FTSResultRow = Database['public']['Functions']['search_books_fts']['Returns'][number];

/** Core book data - clean API shape */
export interface BookCore {
  id: string;
  title: string;
  author: string;
  price: number;
  mrp?: number;
  previous_price?: number;
  price_updated_at?: string;
  condition: string;
  category: string;
  description?: string;
  images?: string[];
  seller_id: string;
  status: BookStatus;
  featured?: boolean;
  created_at: string;
  language?: string;
}

export type BookSort = 'newest' | 'price_asc' | 'price_desc' | 'rating';

export interface BookFilters {
  languages?: string[];
  conditions?: string[];
  priceMin?: number;
  priceMax?: number;
  minSellerRating?: number; // e.g. 4
  sort?: BookSort;

}

/** Book with flattened seller info - preferred for new code */
export interface BookWithSeller extends BookCore {
  seller_name?: string;
  seller_pin_code?: string;
  seller_created_at?: string;
  seller_avg_rating?: number;
  seller_review_count?: number;
}

/** Legacy seller shape for backward compatibility */
export interface SellerInfo {
  name: string;
  pin_code?: string;
}

/** 
 * Full Book type with legacy seller object
 * @deprecated Use BookWithSeller for new code; seller object kept for backward compat
 */
export interface Book extends BookWithSeller {
  seller?: SellerInfo;
}

export interface PaginationOptions {
  limit?: number;
  offset?: number;
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
}

const DEFAULT_LIMIT = 20;
const MAX_LIMIT = 100;

// Common fields for normalization - shared between view and FTS results
interface NormalizableRow {
  id: string | null;
  title: string | null;
  author: string | null;
  price: number | null;
  condition: string | null;
  category: string | null;
  description: string | null;
  images: string[] | null;
  seller_id: string | null;
  status: string | null;
  featured: boolean | null;
  created_at: string | null;
  seller_name: string | null;
  seller_created_at?: string | null;
  seller_avg_rating?: number | null;
  seller_review_count?: number | null;
  previous_price?: number | null;
  price_updated_at?: string | null;
  language?: string | null;
}

// Internal helper to validate and build Book from common row shape
// Fails fast on missing required fields to surface data integrity issues
function buildBookFromRow(
  row: NormalizableRow,
  sellerPinCode: string | undefined,
  sellerCreatedAt?: string | undefined
): Book {
  // Validate required fields - fail fast if data is corrupt
  if (!row.id || !row.title || !row.author || !row.status || !row.created_at || row.price == null) {
    throw new DataIntegrityError(`missing required fields (id=${row.id})`, 'Book');
  }
  
  // seller_id is required - books must always have a seller
  if (!row.seller_id) {
    throw new DataIntegrityError(`missing seller_id (id=${row.id})`, 'Book');
  }

  return {
    id: row.id,
    title: row.title,
    author: row.author,
    price: row.price,
    previous_price: row.previous_price ?? undefined,
    price_updated_at: row.price_updated_at ?? undefined,
    condition: row.condition ?? '',
    category: row.category ?? '',
    description: row.description ?? undefined,
    images: row.images ?? undefined,
    seller_id: row.seller_id,
    status: row.status as BookStatus,
    featured: row.featured ?? undefined,
    created_at: row.created_at,
    language: row.language ?? undefined,
    seller_name: row.seller_name ?? undefined,
    seller_pin_code: sellerPinCode,
    seller_created_at: sellerCreatedAt ?? row.seller_created_at ?? undefined,
    seller_avg_rating: row.seller_avg_rating ?? undefined,
    seller_review_count: row.seller_review_count ?? undefined,
    // Legacy shape - only included when seller_name exists to encourage using flattened fields
    ...(row.seller_name && {
      seller: {
        name: row.seller_name,
        pin_code: sellerPinCode,
      },
    }),
  };
}

// Normalize view row to Book interface
function normalizeBook(row: BooksWithSellerRow): Book {
  return buildBookFromRow(
    row, 
    row.seller_pin_code ?? undefined,
    row.seller_created_at ?? undefined
  );
}

// Normalize FTS result row (doesn't include seller_pin_code or seller_created_at)
function normalizeFTSResult(row: FTSResultRow): Book {
  return buildBookFromRow(row, undefined, undefined);
}

// Escape SQL LIKE/ILIKE wildcards and backslashes to prevent abuse
function escapeWildcards(input: string): string {
  return input.replace(/[\\%_]/g, '\\$&');
}

// Circuit breaker for FTS - prevents hammering fallback on repeated failures
const ftsCircuitBreaker = {
  failures: 0,
  lastFailure: 0,
  threshold: 3,           // Open circuit after 3 consecutive failures
  cooldownMs: 30 * 1000,  // Stay in fallback mode for 30 seconds
  
  isOpen(): boolean {
    if (this.failures < this.threshold) return false;
    // Check if cooldown has passed
    if (Date.now() - this.lastFailure > this.cooldownMs) {
      this.reset();
      return false;
    }
    return true;
  },
  
  recordFailure(): void {
    this.failures++;
    this.lastFailure = Date.now();
  },
  
  reset(): void {
    this.failures = 0;
    this.lastFailure = 0;
  }
};

export const booksService = {
  // Fetch all active books with optional pagination
  async getAllBooks({ limit = DEFAULT_LIMIT, offset = 0 }: PaginationOptions = {}): Promise<Book[]> {
    const { data } = await this.getAllBooksWithCount({ limit, offset });
    return data;
  },

  // Fetch all active books with total count for pagination UIs
  async getAllBooksWithCount(
    { limit = DEFAULT_LIMIT, offset = 0 }: PaginationOptions = {},
    filters: BookFilters = {}
  ): Promise<PaginatedResult<Book>> {
    const safeLimit = Math.min(limit, MAX_LIMIT);
    let q = supabase
      .from('books_with_seller')
      .select('*', { count: 'exact' })
      .in('status', [BOOK_STATUS.ACTIVE, BOOK_STATUS.SOLD]);

    q = applyBookFilters(q, filters);
    q = applyBookSort(q, filters.sort);
    const { data, error, count } = await q.range(offset, offset + safeLimit - 1);

    if (error) throw new DatabaseError('Failed to fetch books', error);
    return { data: (data || []).map(normalizeBook), total: count ?? 0 };
  },

  // Fetch featured books (falls back to recent books if no featured exist)
  async getFeaturedBooks(limit = 8): Promise<Book[]> {
    // First try to get manually featured books
    const { data: featuredData, error: featuredError } = await supabase
      .from('books_with_seller')
      .select('*')
      .eq('status', BOOK_STATUS.ACTIVE)
      .eq('featured', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (featuredError) throw new DatabaseError('Failed to fetch featured books', featuredError);
    
    // If we have featured books, return them
    if (featuredData && featuredData.length > 0) {
      return featuredData.map(normalizeBook);
    }

    // Fallback: get most recent active books
    const { data: recentData, error: recentError } = await supabase
      .from('books_with_seller')
      .select('*')
      .eq('status', BOOK_STATUS.ACTIVE)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (recentError) throw new DatabaseError('Failed to fetch recent books', recentError);
    return (recentData || []).map(normalizeBook);
  },

  // Fetch book by ID - uses books_full view for single query with mrp + pin_code
  async getBookById(id: string): Promise<Book | null> {
    // Validate UUID format using shared utility
    validateUUID(id, 'book ID');
    
    // Single query via books_full view - includes mrp, seller name, and pin_code
    const { data: bookData, error: bookError } = await supabase
      .from('books_full')
      .select('*')
      .eq('id', id)
      .eq('status', BOOK_STATUS.ACTIVE)
      .maybeSingle();

    if (bookError) {
      logger.error('booksService.getBookById: Database error:', bookError);
      throw new DatabaseError('Failed to fetch book details', bookError);
    }
    
    if (!bookData) {
      // Debug log only in development - avoid log noise in production
      if (import.meta.env.DEV) {
        logger.warn('booksService.getBookById: No book found with ID:', id);
      }
      return null;
    }
    
    const sellerName = bookData.seller_name || 'Seller';
    let sellerPinCode = bookData.seller_pin_code ?? undefined;

    // Fallback: if the view didn't surface a pin_code (e.g. RLS edge case or
    // stale data), fetch it via the SECURITY DEFINER RPC used by checkout so
    // the shipping calculator stays consistent across the buyer flow.
    if (!sellerPinCode && bookData.seller_id) {
      try {
        const { data: rpcPin } = await supabase.rpc('get_seller_pincode', {
          _seller_id: bookData.seller_id,
        });
        if (typeof rpcPin === 'string' && /^\d{6}$/.test(rpcPin)) {
          sellerPinCode = rpcPin;
        }
      } catch (fallbackErr) {
        logger.debug('[booksService] get_seller_pincode fallback failed', fallbackErr);
      }
    }
    
    return {
      id: bookData.id,
      title: bookData.title,
      author: bookData.author,
      price: bookData.price,
      mrp: bookData.mrp ?? undefined,
      previous_price: bookData.previous_price ?? undefined,
      price_updated_at: bookData.price_updated_at ?? undefined,
      condition: bookData.condition,
      category: bookData.category,
      description: bookData.description ?? undefined,
      images: bookData.images ?? undefined,
      seller_id: bookData.seller_id || (() => { throw new DataIntegrityError(`missing seller_id (id=${bookData.id})`, 'Book'); })(),
      status: bookData.status as BookStatus,
      featured: bookData.featured ?? undefined,
      created_at: bookData.created_at,
      seller_name: sellerName,
      seller_pin_code: sellerPinCode,
      seller_avg_rating: bookData.seller_avg_rating ?? undefined,
      seller_review_count: bookData.seller_review_count ?? undefined,
      seller: {
        name: sellerName,
        pin_code: sellerPinCode,
      },
    };
  },

  // Fetch books by category slug (preferred - decouples URL slugs from DB values)
  async getBooksByCategorySlug(
    slug: string,
    { limit = DEFAULT_LIMIT, offset = 0 }: PaginationOptions = {}
  ): Promise<Book[]> {
    const { data } = await this.getBooksByCategorySlugWithCount(slug, { limit, offset });
    return data;
  },

  // Fetch books by category slug with total count for pagination UIs
  async getBooksByCategorySlugWithCount(
    slug: string,
    { limit = DEFAULT_LIMIT, offset = 0 }: PaginationOptions = {},
    filters: BookFilters = {}
  ): Promise<PaginatedResult<Book>> {
    const { getDbCategoryFromSlug } = await import('@/utils/categoryUtils');
    const dbCategory = getDbCategoryFromSlug(slug);

    // If slug doesn't map to a known category, return empty
    if (!dbCategory) return { data: [], total: 0 };

    return this.getBooksByCategoryWithCount(dbCategory, { limit, offset }, filters);
  },

  // Fetch books by category name (includes subcategories for main categories)
  async getBooksByCategory(
    category: string,
    { limit = DEFAULT_LIMIT, offset = 0 }: PaginationOptions = {}
  ): Promise<Book[]> {
    const { data } = await this.getBooksByCategoryWithCount(category, { limit, offset });
    return data;
  },

  // Fetch books by category name with total count for pagination UIs
  async getBooksByCategoryWithCount(
    category: string,
    { limit = DEFAULT_LIMIT, offset = 0 }: PaginationOptions = {},
    filters: BookFilters = {}
  ): Promise<PaginatedResult<Book>> {
    const categoriesToSearch = isMainCategory(category)
      ? getSubcategoriesForMainCategory(category)
      : [category];

    // Guard against empty category lists
    if (!categoriesToSearch.length) return { data: [], total: 0 };

    const safeLimit = Math.min(limit, MAX_LIMIT);
    let q = supabase
      .from('books_with_seller')
      .select('*', { count: 'exact' })
      .in('status', [BOOK_STATUS.ACTIVE, BOOK_STATUS.SOLD])
      .in('category', categoriesToSearch);

    q = applyBookFilters(q, filters);
    q = applyBookSort(q, filters.sort);
    const { data, error, count } = await q.range(offset, offset + safeLimit - 1);

    if (error) throw new DatabaseError('Failed to fetch books by category', error);
    return { data: (data || []).map(normalizeBook), total: count ?? 0 };
  },

  // Search books using Postgres full-text search with circuit-breaker and pagination
  async searchBooks(
    query: string,
    { limit = DEFAULT_LIMIT, offset = 0 }: PaginationOptions = {},
    filters: BookFilters = {}
  ): Promise<PaginatedResult<Book>> {
    const normalizedQuery = query.trim();

    if (!normalizedQuery) {
      return this.getAllBooksWithCount({ limit, offset }, filters);
    }

    // Skip FTS if filters require columns absent from the FTS result (language, seller rating)
    const requiresFallbackFilters =
      (filters.languages && filters.languages.length > 0) ||
      (typeof filters.minSellerRating === 'number' && filters.minSellerRating > 0);

    if (requiresFallbackFilters || ftsCircuitBreaker.isOpen()) {
      if (ftsCircuitBreaker.isOpen()) {
        logger.warn('FTS circuit breaker open, using fallback directly');
      }
      return this.searchBooksFallbackWithCount(normalizedQuery, { limit, offset }, filters);
    }

    // Full-text search RPC. Bound the fetched payload to avoid pulling huge
    // result sets into the browser (RPC has no server-side pagination), then
    // apply filters/sort/slice on the bounded window.
    const FTS_MAX_ROWS = Math.max(MAX_LIMIT, offset + limit) * 3;
    const { data, error } = await supabase
      .rpc('search_books_fts', { search_query: normalizedQuery })
      .range(0, FTS_MAX_ROWS - 1);

    if (error) {
      ftsCircuitBreaker.recordFailure();
      logger.warn(`FTS failed (${ftsCircuitBreaker.failures}/${ftsCircuitBreaker.threshold}), falling back:`, error.message);
      return this.searchBooksFallbackWithCount(normalizedQuery, { limit, offset }, filters);
    }

    // Success - reset circuit breaker
    ftsCircuitBreaker.reset();

    // Normalize and apply client-side filters supported by FTS result shape
    let results = (data || []).map((row) => normalizeFTSResult(row));

    if (filters.conditions && filters.conditions.length > 0) {
      results = results.filter((b) => filters.conditions!.includes(b.condition));
    }
    if (typeof filters.priceMin === 'number' && Number.isFinite(filters.priceMin)) {
      results = results.filter((b) => b.price >= filters.priceMin!);
    }
    if (typeof filters.priceMax === 'number' && Number.isFinite(filters.priceMax)) {
      results = results.filter((b) => b.price <= filters.priceMax!);
    }

    results = applyClientSort(results, filters.sort);

    const total = results.length;
    const paginated = results.slice(offset, offset + limit);
    return { data: paginated, total };
  },

  // Fallback search using ilike (used when FTS fails or for edge cases)
  async searchBooksFallback(
    query: string,
    { limit = DEFAULT_LIMIT, offset = 0 }: PaginationOptions = {}
  ): Promise<Book[]> {
    const { data } = await this.searchBooksFallbackWithCount(query, { limit, offset });
    return data;
  },

  // Fallback search with total count for pagination UIs
  async searchBooksFallbackWithCount(
    query: string,
    { limit = DEFAULT_LIMIT, offset = 0 }: PaginationOptions = {},
    filters: BookFilters = {}
  ): Promise<PaginatedResult<Book>> {
    const safeLimit = Math.min(limit, MAX_LIMIT);
    const safeQuery = escapeWildcards(query);
    let q = supabase
      .from('books_with_seller')
      .select('*', { count: 'exact' })
      .in('status', [BOOK_STATUS.ACTIVE, BOOK_STATUS.SOLD])
      .or(`title.ilike.%${safeQuery}%,author.ilike.%${safeQuery}%,category.ilike.%${safeQuery}%,description.ilike.%${safeQuery}%`);

    q = applyBookFilters(q, filters);
    q = applyBookSort(q, filters.sort);
    const { data, error, count } = await q.range(offset, offset + safeLimit - 1);

    if (error) throw new DatabaseError('Failed to search books', error);
    return { data: (data || []).map(normalizeBook), total: count ?? 0 };
  }
};

// ---------- Filter / sort helpers ----------

function applyBookFilters<T extends { in: Function; gte: Function; lte: Function }>(
  q: T,
  f: BookFilters
): T {
  if (f.languages && f.languages.length > 0) {
    q = q.in('language', f.languages) as T;
  }
  if (f.conditions && f.conditions.length > 0) {
    q = q.in('condition', f.conditions) as T;
  }
  if (typeof f.priceMin === 'number' && Number.isFinite(f.priceMin)) {
    q = q.gte('price', f.priceMin) as T;
  }
  if (typeof f.priceMax === 'number' && Number.isFinite(f.priceMax)) {
    q = q.lte('price', f.priceMax) as T;
  }
  if (typeof f.minSellerRating === 'number' && f.minSellerRating > 0) {
    q = q.gte('seller_avg_rating', f.minSellerRating) as T;
  }
  return q;
}

function applyBookSort<T extends { order: Function }>(q: T, sort?: BookSort): T {
  // Always push sold listings to the bottom regardless of the user's chosen sort.
  // 'active' < 'sold' alphabetically, so ascending status puts active first.
  q = q.order('status', { ascending: true }) as T;
  switch (sort) {
    case 'price_asc':
      return q.order('price', { ascending: true }) as T;
    case 'price_desc':
      return q.order('price', { ascending: false }) as T;
    case 'rating':
      return q.order('seller_avg_rating', { ascending: false, nullsFirst: false }) as T;
    case 'newest':
    default:
      return q.order('created_at', { ascending: false }) as T;
  }
}


function applyClientSort(books: Book[], sort?: BookSort): Book[] {
  const sorted = [...books];
  const primary = (a: Book, b: Book) => {
    // Push sold to the bottom regardless of user sort.
    const aSold = a.status === BOOK_STATUS.SOLD ? 1 : 0;
    const bSold = b.status === BOOK_STATUS.SOLD ? 1 : 0;
    return aSold - bSold;
  };
  const cmp = (sec: (a: Book, b: Book) => number) =>
    (a: Book, b: Book) => primary(a, b) || sec(a, b);
  switch (sort) {
    case 'price_asc':
      return sorted.sort(cmp((a, b) => a.price - b.price));
    case 'price_desc':
      return sorted.sort(cmp((a, b) => b.price - a.price));
    case 'rating':
      return sorted.sort(cmp((a, b) => (b.seller_avg_rating || 0) - (a.seller_avg_rating || 0)));
    case 'newest':
    default:
      return sorted.sort(cmp((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
  }
}


